import React, { useEffect, useState } from 'react';
import api from './api';
import Modal from './components/Modal';
import Sticker from './components/Sticker';
import toast from 'react-hot-toast';
import { FaEdit, FaPlus, FaSearch, FaCertificate, FaUnlock, FaLock } from 'react-icons/fa';

const Licenses = () => {
    const [licenses, setLicenses] = useState([]);
    const [users, setUsers] = useState([]);
    const [allApps, setAllApps] = useState([]);
    const [search, setSearch] = useState('');
    const [userRole] = useState(JSON.parse(localStorage.getItem('user') || '{}').role);
    const [userFilter, setUserFilter] = useState('');
    const isAdmin = userRole === 'admin' || userRole === 'super_admin';

    const [formData, setFormData] = useState({ 
        user_id: '', 
        customer_name: '',
        agent_name: '',
        email: '',
        account_manager: '',
        bypass_global_blocks: false,
        device_limit: 1, 
        expiry_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0] 
    });
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isStickerModalOpen, setStickerModalOpen] = useState(false);
    const [isAppModalOpen, setAppModalOpen] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [licenseOverrides, setLicenseOverrides] = useState({});

    useEffect(() => { 
        fetchLicenses(); 
        fetchApps();
        if (isAdmin) fetchUsers();
    }, [isAdmin]);

    const fetchLicenses = async (searchVal = '') => {
        try {
            const { data } = await api.get(`/licenses?search=${searchVal}`);
            setLicenses(data);
        } catch (err) { }
    };

    const fetchUsers = async () => {
        try {
            const { data } = await api.get('/users');
            setUsers(data);
        } catch (err) { }
    };

    const fetchApps = async () => {
        try {
            const { data } = await api.get('/apps');
            setAllApps(data);
        } catch (err) {}
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        fetchLicenses(e.target.value);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.user_id) return toast.error('Please select a user');
        setLoading(true);
        try {
            if (editingId) {
                await api.put(`/licenses/${editingId}`, formData);
                toast.success('License updated successfully');
            } else {
                await api.post('/licenses', formData);
                toast.success('License generated successfully');
            }
            fetchLicenses(search);
            setModalOpen(false);
            setFormData({ 
                user_id: '', 
                customer_name: '',
                agent_name: '',
                email: '',
                account_manager: '',
                bypass_global_blocks: false,
                device_limit: 1, 
                expiry_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0] 
            });
            setEditingId(null);
        } catch (err) {
            toast.error(editingId ? 'Failed to update license' : 'Failed to generate license');
        } finally {
            setLoading(false);
        }
    };

    const openEdit = (l) => {
        setFormData({
            ...l,
            bypass_global_blocks: !!l.bypass_global_blocks,
            expiry_date: new Date(l.expiry_date).toISOString().split('T')[0]
        });
        setEditingId(l.id);
        setModalOpen(true);
    };

    const toggleStatus = async (l) => {
        try {
            const newStatus = l.status === 'active' ? 'suspended' : 'active';
            await api.put(`/licenses/${l.id}`, { ...l, status: newStatus });
            fetchLicenses(search);
            toast.success(`License ${newStatus}`);
        } catch (err) {
            toast.error('Operation failed');
        }
    };

    const showSticker = (l) => {
        setSelectedLicense(l);
        setStickerModalOpen(true);
    };

    const openAppManager = async (l) => {
        setSelectedLicense(l);
        try {
            const { data } = await api.get(`/apps/override/${l.id}`);
            const overrides = {};
            data.forEach(o => overrides[o.app_id] = o.is_blocked);
            setLicenseOverrides(overrides);
            setAppModalOpen(true);
        } catch (err) { toast.error('Failed to load overrides'); }
    };

    const toggleAppOverride = async (appId, currentStatus) => {
        const newStatus = !currentStatus;
        try {
            await api.post('/apps/override', { license_id: selectedLicense.id, app_id: appId, is_blocked: newStatus });
            setLicenseOverrides({ ...licenseOverrides, [appId]: newStatus });
            toast.success(`App ${newStatus ? 'Blocked' : 'Allowed'} for License`);
        } catch (err) { toast.error('Failed to update override'); }
    };

    return (
        <div className="module">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' }}>
                <h2 style={{ margin: 0 }}>License Management</h2>
                
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <div className="search-bar" style={{ position: 'relative' }}>
                        <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#45a29e' }} />
                        <input 
                            placeholder="Customer, Email, BE Number..." 
                            value={search}
                            onChange={handleSearch}
                            style={{ paddingLeft: '40px', width: '250px', margin: 0 }}
                        />
                    </div>

                    {isAdmin && (
                        <select 
                            value={userFilter} 
                            onChange={e => setUserFilter(e.target.value)}
                            style={{ width: 'auto', background: '#1f2833', color: '#fff', border: '1px solid #45a29e', borderRadius: '4px', padding: '0 10px', height: '40px' }}
                        >
                            <option value="">All Admins/Users</option>
                            {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                        </select>
                    )}

                    {isAdmin && (
                        <button onClick={() => { setEditingId(null); setModalOpen(true); }} style={{ width: 'auto', margin: 0, background: '#66fcf1', color: '#0b0c10' }}>
                            <FaPlus /> Generate License
                        </button>
                    )}
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Customer & Admin</th>
                            <th>BE Number</th>
                            <th>Agent Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {licenses
                            .filter(l => userFilter === '' || l.user_name === userFilter)
                            .map(l => (
                            <tr key={l.id}>
                                <td>{new Date(l.created_at).toLocaleDateString()}</td>
                                <td>
                                    <div style={{ color: '#66fcf1', fontWeight: 'bold' }}>{l.customer_name}</div>
                                    <small style={{ color: '#aaa', fontSize: '0.75rem' }}>Assigned: {l.user_name}</small>
                                </td>
                                <td style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>{l.license_key}</td>
                                <td>{l.agent_name}</td>
                                <td>{l.email}</td>
                                <td><span className={`status ${l.status}`}>{l.status}</span></td>
                                <td>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {isAdmin && (
                                            <>
                                                <button onClick={() => openAppManager(l)} style={{ width: 'auto', padding: '5px 10px', fontSize: '0.75rem', background: 'transparent', border: '1px solid #45a29e', color: '#45a29e' }}>
                                                    <FaUnlock /> Apps
                                                </button>
                                                <button onClick={() => openEdit(l)} style={{ width: 'auto', padding: '5px 10px', fontSize: '0.75rem', background: 'transparent', border: '1px solid #66fcf1', color: '#66fcf1' }}>
                                                    <FaEdit />
                                                </button>
                                                <button onClick={() => toggleStatus(l)} style={{ width: 'auto', padding: '5px 10px', fontSize: '0.75rem', background: l.status === 'active' ? '#ff4d4d' : '#45a29e' }}>
                                                    {l.status === 'active' ? 'Suspend' : 'Extend'}
                                                </button>
                                            </>
                                        )}
                                        <button onClick={() => showSticker(l)} style={{ width: 'auto', padding: '5px 10px', fontSize: '0.75rem', background: '#1f2833', border: '1px solid #66fcf1' }}>
                                            <FaCertificate /> Sticker
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit License Modal */}
            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} title={editingId ? "Edit BE License" : "Generate New BE License"}>
                <form onSubmit={handleSave}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <label>Assigned Admin/User</label>
                            <select 
                                value={formData.user_id} 
                                onChange={e => {
                                    const selectedId = e.target.value;
                                    const selectedUser = users.find(u => u.id === parseInt(selectedId));
                                    setFormData({ 
                                        ...formData, 
                                        user_id: selectedId,
                                        customer_name: (selectedUser && !editingId) ? selectedUser.name : formData.customer_name,
                                        email: (selectedUser && !editingId) ? selectedUser.email : formData.email
                                    });
                                }} 
                                required
                                style={{ width: '100%', marginBottom: '15px', background: '#0b0c10', color: '#fff', border: '1px solid #45a29e', padding: '10px' }}
                            >
                                <option value="">-- Select User --</option>
                                {users
                                    .filter(u => !licenses.some(l => l.user_id === u.id) || (editingId && Number(formData.user_id) === u.id))
                                    .map(u => (
                                        <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                                    ))}
                            </select>
                        </div>
                        <div>
                            <label>Customer Name</label>
                            <input value={formData.customer_name} onChange={e => setFormData({ ...formData, customer_name: e.target.value })} required />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                        <div>
                            <label>Agent Name</label>
                            <input value={formData.agent_name} onChange={e => setFormData({ ...formData, agent_name: e.target.value })} required />
                        </div>
                        <div>
                            <label>Account Manager</label>
                            <input value={formData.account_manager} onChange={e => setFormData({ ...formData, account_manager: e.target.value })} placeholder="Optional" />
                        </div>
                    </div>

                    <label style={{ marginTop: '10px' }}>Customer Email</label>
                    <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                        <div>
                            <label>Device Limit</label>
                            <input type="number" value={formData.device_limit} onChange={e => setFormData({ ...formData, device_limit: e.target.value })} required />
                        </div>
                        <div>
                            <label>Expiry Date</label>
                            <input type="date" value={formData.expiry_date} onChange={e => setFormData({ ...formData, expiry_date: e.target.value })} required />
                        </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#c5c6c7', marginTop: '20px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={formData.bypass_global_blocks}
                            onChange={e => setFormData({ ...formData, bypass_global_blocks: e.target.checked })}
                            style={{ width: '20px', margin: 0 }}
                        />
                        Global APP Bypass (Unlock ALL globally blocked apps for this BE Number)
                    </label>

                    <button disabled={loading} style={{ width: '100%', marginTop: '20px' }}>
                        {loading ? 'Saving...' : editingId ? 'Update License' : 'Register & Generate BE Key'}
                    </button>
                </form>
            </Modal>

            <Modal isOpen={isStickerModalOpen} onClose={() => setStickerModalOpen(false)} title="License Certificate / Sticker">
                <Sticker license={selectedLicense} />
            </Modal>

            {/* App Override Modal for License */}
            <Modal isOpen={isAppModalOpen} onClose={() => setAppModalOpen(false)} title={`App Security: BE ${selectedLicense?.license_key}`}>
                <h3 style={{ borderBottom: '1px solid #333', paddingBottom: '10px', color: '#c5c6c7' }}>Individual Application Rights</h3>
                <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '20px' }}>
                    {selectedLicense?.bypass_global_blocks 
                        ? <span style={{ color: '#ff4d4d' }}>Warning: Global Bypass is ON. Devices using this license ignore all app blocks.</span>
                        : "Toggle specific exceptions below for devices connected to this BE Number."}
                </p>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table style={{ margin: 0 }}>
                        <thead>
                            <tr>
                                <th>Application Name</th>
                                <th>Process Executable</th>
                                <th style={{ textAlign: 'center' }}>License Permission</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allApps.map(app => {
                                const isBlocked = licenseOverrides[app.id] !== undefined ? licenseOverrides[app.id] : true;
                                return (
                                    <tr key={app.id}>
                                        <td style={{ color: '#66fcf1' }}>{app.app_name}</td>
                                        <td style={{ fontFamily: 'monospace' }}>{app.process_name}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button 
                                                onClick={() => toggleAppOverride(app.id, isBlocked)}
                                                style={{ 
                                                    width: '120px', 
                                                    margin: '0 auto',
                                                    padding: '5px 10px', 
                                                    background: isBlocked ? 'rgba(255, 77, 77, 0.1)' : 'rgba(0, 255, 136, 0.1)',
                                                    border: `1px solid ${isBlocked ? '#ff4d4d' : '#00ff88'}`,
                                                    color: isBlocked ? '#ff4d4d' : '#00ff88'
                                                }}
                                            >
                                                {isBlocked ? <><FaLock /> BLOCKED</> : <><FaUnlock /> ALLOWED</>}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {allApps.length === 0 && (
                                <tr>
                                    <td colSpan="3" style={{ textAlign: 'center', color: '#888' }}>No global applications are currently blocked.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Modal>
        </div>
    );
};

export default Licenses;
